// src/components/users/UserManagement.js
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useNotification } from '../../hooks/useNotification';

const UserManagement = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const [roleFilter, setRoleFilter] = useState('all'); // all, user, admin, journalist

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/users?includeInactive=true');
      let filteredUsers = response.data.users || [];
      
      // Apply filters
      if (filter === 'active') {
        filteredUsers = filteredUsers.filter(u => u.is_active);
      } else if (filter === 'inactive') {
        filteredUsers = filteredUsers.filter(u => !u.is_active);
      }
      
      if (roleFilter !== 'all') {
        filteredUsers = filteredUsers.filter(u => u.role === roleFilter);
      }
      
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      const errorMsg = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.response?.data?.error?.message || 'Failed to load users';
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [filter, roleFilter, showError]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Auto-refresh users every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadUsers();
    }, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [loadUsers]);


  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.patch(`/api/users/${userId}/role`, { role: newRole });
      showSuccess(`User role updated to ${newRole}`);
      loadUsers();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.response?.data?.error?.message || 'Failed to update role';
      showError(errorMsg);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }
    
    try {
      await api.patch(`/api/users/${userId}/deactivate`);
      showSuccess('User deactivated successfully');
      loadUsers();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.response?.data?.error?.message || 'Failed to deactivate user';
      showError(errorMsg);
    }
  };

  const handleActivate = async (userId) => {
    if (!window.confirm('Activating this user will require them to verify their email. Continue?')) {
      return;
    }
    
    try {
      const response = await api.patch(`/api/users/${userId}/activate`);
      if (response.data.requiresVerification) {
        showSuccess('User activated successfully. A verification email has been sent to the user.');
      } else {
        showSuccess('User activated successfully');
      }
      loadUsers();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.response?.data?.error?.message || 'Failed to activate user';
      showError(errorMsg);
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/api/users/${userId}`);
      showSuccess('User deleted successfully');
      loadUsers();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.response?.data?.error?.message || 'Failed to delete user';
      showError(errorMsg);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': 
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'journalist': 
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default: 
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return t('roles.admin', 'Admin');
      case 'journalist': return t('dashboard.journalistTeam');
      default: return t('roles.user', 'User');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900">
        {t('dashboard.journalistTeam')} – User Management
      </h2>
      {/* Filters */}
      <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">Status</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
            >
              <option value="all">All</option>
              <option value="user">{t('roles.user', 'User')}</option>
              <option value="journalist">{t('dashboard.journalistTeam')}</option>
              <option value="admin">{t('roles.admin', 'Admin')}</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilter('all');
                setRoleFilter('all');
              }}
              className="w-full px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 bg-shayek-light-100 dark:bg-shayek-dark-50 rounded-lg hover:bg-shayek-light-200 dark:hover:bg-shayek-dark-100 transition-colors border border-shayek-light-300 dark:border-shayek-dark-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <div className="p-6 border-b border-shayek-light-300 dark:border-shayek-dark-200">
          <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900">Users ({users.length})</h2>
        </div>
        {loading ? (
          <div className="p-6 text-center text-shayek-light-500 dark:text-shayek-dark-500">Loading...</div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center text-shayek-light-500 dark:text-shayek-dark-500">No users found</div>
        ) : (
          <div className="divide-y divide-shayek-light-200 dark:divide-shayek-dark-200">
            {users.map((user) => (
              <div
                key={user.id}
                className="p-6 hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-shayek-light-900 dark:text-shayek-dark-900">
                        {user.email}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getRoleBadgeColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {!user.email_verified && (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                          Unverified
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
                      Created: {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      className="px-3 py-1 text-sm border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
                    >
                      <option value="user">User</option>
                      <option value="journalist">{t('dashboard.journalistTeam')}</option>
                      <option value="admin">Admin</option>
                    </select>
                    {user.is_active ? (
                      <button
                        onClick={() => handleDeactivate(user.id)}
                        className="px-4 py-1 text-sm bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/30 transition-colors"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivate(user.id)}
                        className="px-4 py-1 text-sm bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/30 transition-colors"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(user.id, user.email)}
                      className="px-4 py-1 text-sm bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;

